import React, { useCallback, useEffect, useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { PassiveBorderTransportMeansType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist, { createTypeOfIdentificationsByModeOfTransportFilter } from 'features/declaration/codelist/api/useCodelist'
import useCodelistOptions from 'features/declaration/codelist/components/useCodelistOptions'
import { blankPassiveBorderTransportMeans } from 'features/declaration/models/blankItems'
import E1103Validator from 'features/declaration/form/validations/E1103'
import FormCollapsibleColumn from '../../../../../../components/p5/FormCollapsibleColumn'
import Input from '../../../../../../components/p5/Input'
import FormFieldArray from '../../../../../../components/p5/FormFieldArray'
import FormSelect from '../../../../../../components/p5/FormSelect'
import FormField from '../../../../../../components/p5/FormField'
import useDeclarationFieldRequirements
  from '../../../components/useDeclarationFieldRequirements'

const scope = 'passiveBorderTransportMeans'

interface PassiveBorderTransportMeansProps {
  borderModeOfTransport: string
  entriesGrid?: boolean
}

function EnsPassiveBorderTransportMeans({
  borderModeOfTransport,
  entriesGrid = false,
} : Readonly<PassiveBorderTransportMeansProps>) {
  const { t } = useTranslation()

  const {
    control,
    setValue,
  } = useFormContext<ConsignmentItemFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<ConsignmentItemFormValues, PassiveBorderTransportMeansType>({
    control,
    name: scope,
  })

  const differentPassiveBorderTransportMeans = useWatch({
    control,
    name: 'differentPassiveBorderTransportMeans',
  })

  const [, countriesOptions] = useCodelist('COUNTRIES')
  const [, typeOfMeansOfTransportOptions] = useCodelist('TRANSPORT_MEANS_TYPES')
  const transportIdentificationsFilterPredicate = useMemo(
    () => createTypeOfIdentificationsByModeOfTransportFilter(borderModeOfTransport),
    [borderModeOfTransport],
  )
  const {
    options: transportIdentificationsOptions,
    isSuccess: isTransportIdentificationsLoaded,
  } = useCodelistOptions('TRANSPORT_IDENTIFICATIONS', transportIdentificationsFilterPredicate)

  useEffect(() => {
    if (!isTransportIdentificationsLoaded) {
      return
    }

    visibleFields.forEach((fieldWithIndex) => {
      const item = fieldWithIndex.field
      const hasCorrectSubType = transportIdentificationsOptions.find((option) => option.value === item.typeOfIdentification)
      const innerItemScope = `${scope}.${fieldWithIndex.index}` as const
      if (!borderModeOfTransport || !hasCorrectSubType) {
        setValue(`${innerItemScope}.typeOfIdentification`, '', {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    })
  }, [visibleFields, setValue, borderModeOfTransport, isTransportIdentificationsLoaded, transportIdentificationsOptions])

  const addNew = useCallback(() => appendWithSequence(blankPassiveBorderTransportMeans), [appendWithSequence])

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((f) => `${scope}.${f.index}.typeOfIdentification`),
      ...visibleFields.map((f) => `${scope}.${f.index}.identificationNumber`),
      ...visibleFields.map((f) => `${scope}.${f.index}.nationality`),
      ...visibleFields.map((f) => `${scope}.${f.index}.typeOfMeansOfTransport`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<ConsignmentItemFormValues>('consignmentItem', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-deprture-transport"
        headingLabel={t('declaration.p5.borderTransportMeans', { context: 'passive' })}
        fieldRule={fieldRequirements[scope]}
      >
        <div className="row mb-2">
          <div className="col">
            <Input
              field="differentPassiveBorderTransportMeans"
              type="checkbox"
              labelKey="Different passive border transport means from house consignment"
            />
          </div>
        </div>
        {differentPassiveBorderTransportMeans && (
          <>
            <div className={entriesGrid ? 'transport-entries-grid' : undefined}>
              {visibleFields.map((fieldWithIndex) => {
                const borderTransportMeans = fieldWithIndex.field
                const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

                return (
                  <FormFieldArray
                    key={`borderTransportMeans_${borderTransportMeans.id}`}
                    titleKey="declaration.p5.sequenceNumber"
                    removeItem={() => softRemove(fieldWithIndex.index)}
                    sequenceNumber={borderTransportMeans.sequenceNumber + 1}
                    hidden={borderTransportMeans.deleted}
                  >
                    <FormField
                      labelKey="declaration.p5.typeOfIdentification"
                      fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
                    >
                      <FormSelect
                        <ConsignmentItemFormValues>
                        field={`${innerItemScope}.typeOfIdentification`}
                        labelKey="declaration.p5.typeOfIdentification"
                        options={transportIdentificationsOptions}
                        isDisabled={!borderModeOfTransport}
                        type="sync"
                        fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
                      />
                    </FormField>
                    <FormField
                      labelKey="declaration.p5.identificationNumber"
                      fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
                    >
                      <Input
                        <ConsignmentItemFormValues>
                        field={`${innerItemScope}.identificationNumber`}
                        labelKey="declaration.p5.identificationNumber"
                        type="text"
                        maxLength={E1103Validator.getIdentificationNumberMaxLength()}
                        placeholder={t('declaration.p5.identificationNumber')}
                        fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
                      />
                    </FormField>
                    <FormField
                      labelKey="declaration.p5.nationality"
                      fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
                    >
                      <FormSelect
                        <ConsignmentItemFormValues>
                        field={`${innerItemScope}.nationality`}
                        labelKey="declaration.p5.nationality"
                        options={countriesOptions}
                        type="sync"
                        fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
                      />
                    </FormField>
                    <FormField
                      labelKey="declaration.p5.typeOfMeansOfTransport"
                      fieldRule={fieldRequirements[`${innerItemScope}.typeOfMeansOfTransport`]}
                    >
                      <FormSelect
                        <ConsignmentItemFormValues>
                        field={`${innerItemScope}.typeOfMeansOfTransport`}
                        labelKey="declaration.p5.typeOfMeansOfTransport"
                        options={typeOfMeansOfTransportOptions}
                        type="sync"
                        fieldRule={fieldRequirements[`${innerItemScope}.typeOfMeansOfTransport`]}
                      />
                    </FormField>
                  </FormFieldArray>
                )
              })}
            </div>
            <div className="row">
              <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
                <button
                  type="button"
                  className="btn btn-sm btn-primary mt-2"
                  disabled={isAddForbidden}
                  onClick={addNew}
                >
                  <i className="fa fa-plus me-2" />
                  {t('declaration.p5.addNewTransportMeans')}
                </button>
              </div>
            </div>
          </>
        )}
      </FormCollapsibleColumn>
    </section>
  )
}

export default EnsPassiveBorderTransportMeans
